import { Header } from "components/Header"
import { trpc } from "utils/trpc"
import { Link, router, useLocalSearchParams } from "expo-router"
import MIcon from "@react-native-vector-icons/material-design-icons"
import { List } from "@src/components/list"
import { ActivityIndicator, Pressable, View } from "react-native"
import { Alert } from "@src/components/ui/confirm"
import { ar } from "i18n/ar"
import { SafeAreaView } from "react-native-safe-area-context"
import { Text } from "components/core"
import { useNumCols } from "@src/hooks"
import { Image } from "expo-image"
import { ENV } from "@src/utils/env"
import { formatPrice, sharedStyles } from "@src/utils"
import { ImageUploadWrapper } from "@src/components/image-upload-wrapper"
import { useUploadImage } from "@src/hooks/use-product-image-upload"

export default function Brand() {
  const params = useLocalSearchParams<{ id: string }>()
  const brandId = Number.parseInt(params.id, 10)
  const brands = trpc.brands.list.useQuery()
  const brand = brands.data?.find((brand) => brand.id === brandId)
  const numCols = Math.max(2, useNumCols(250, { maxWidth: 1400 }))
  const products = trpc.products.list.useInfiniteQuery({ brandId }, { getNextPageParam: (lastPage) => lastPage.nextCursor })
  const uploadImage = useUploadImage()

  const showProductMenu = (productId: number, productName: string) => {
    Alert.alert(productName, undefined, [
      { text: ar.cancel, style: "cancel" },
      { text: ar.edit, onPress: () => router.navigate(`/dashboard/products/${productId}/edit`) },
    ])
  }
  const image = brand?.images?.at(-1)

  return (
    <>
      <Header
        title={brand?.name as string}
        right={
          <ImageUploadWrapper
            processing={uploadImage.isPending}
            onConfirm={(uri) =>
              uploadImage.mutate(
                { entity: "brand", entityId: brandId, uri },
                {
                  onSuccess() {
                    brands.refetch()
                  },
                },
              )
            }
            className="h-full"
          >
            {image ? (
              <Image source={`${ENV.R2_URL}/${image.key}`} className="h-10 w-10" contentFit="contain" transition={250} loading="lazy" cachePolicy="memory" />
            ) : (
              <View className="h-10 w-10 bg-blue-300" />
            )}
          </ImageUploadWrapper>
        }
      />
      <List
        data={products.data?.pages.flatMap((page) => page.items) ?? []}
        numColumns={numCols}
        key={`products-grid-${numCols}`}
        keyExtractor={(item) => item.id.toString()}
        ListEmptyComponent={products.isLoading ? <ActivityIndicator /> : <Text className="text-center font-bold mt-4">{ar.noProducts}</Text>}
        onEndReached={() => products.fetchNextPage()}
        onRefresh={products.refetch}
        onEndReachedThreshold={0.5}
        contentContainerStyle={sharedStyles.contentContainer}
        renderItem={({ item }) => {
          const showOriginalPrice = item.originalPrice && item.originalPrice > item.price
          const image = item.images?.at(-1)

          return (
            <View className="bg-white p-3 rounded mb-3 shadow-sm mx-1 flex-1 hover:shadow-xl transition-all border border-gray-100">
              <Link href={`/dashboard/products/${item.id}`} asChild>
                <Pressable className="active:opacity-70">
                  {image ? (
                    <Image
                      contentFit="contain"
                      cachePolicy="memory"
                      source={`${ENV.R2_URL}/${image?.key}`}
                      style={{ width: "100%", height: 120, alignSelf: "center" }}
                      recyclingKey={image?.key}
                      transition={250}
                      loading="lazy"
                    />
                  ) : (
                    <View className="w-full h-28 bg-gray-100 rounded items-center justify-center">
                      <MIcon name="image" size={40} color="#9CA3AF" />
                    </View>
                  )}

                  <View className="mt-3">
                    <Text className="text-sm font-semibold text-gray-900 text-center leading-tight" numberOfLines={2} style={{ minHeight: 36 }}>
                      {item.name}
                    </Text>

                    <View className="mt-3 items-center">
                      <Text className="text-base md:text-lg font-bold text-green-600">
                        {formatPrice(item.price)} {ar.egp}
                      </Text>
                      {showOriginalPrice && (
                        <Text className="text-xs md:text-sm text-gray-500 line-through mt-0.5">
                          {formatPrice(item.originalPrice)} {ar.egp}
                        </Text>
                      )}
                    </View>
                  </View>
                </Pressable>
              </Link>

              <Pressable
                onPress={() => showProductMenu(item.id, item.name)}
                className="absolute top-2 right-2 p-1.5 bg-white/80 backdrop-blur-sm hover:bg-white rounded-full shadow-sm"
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <MIcon name="dots-vertical" size={18} color="#6B7280" />
              </Pressable>
            </View>
          )
        }}
        ListFooterComponent={<SafeAreaView />}
      />
    </>
  )
}
